/**
 * Экран набора: используется и диагностикой, и тренировкой.
 *
 * Разница между режимами — только в подсказках и заголовке, поэтому дублировать
 * экран не нужно.
 *
 * Текущие WPM, точность и продвижение по упражнению стоят прямо здесь
 * (ТЗ UX-05): `MetricsBar` и `ProgressBar` ниже. Живой областью для диктора
 * метрики при этом не являются — иначе он читал бы числа на каждое нажатие
 * вместо упражнения (ТЗ UI-29).
 */

import type { HintLevel } from '@type2x/shared-contracts';
import type { Translate } from '@type2x/i18n';
import { fingerForCharacter, getLayout } from '@type2x/keyboard-layouts';
import { Card, KeyboardView, MetricsBar, ProgressBar, TypingView } from '@type2x/ui';
import type { TypingSession } from '@type2x/typing-engine';
import type { IntegritySignals } from '@type2x/adapter-web';
import type { LayoutId, SessionId } from '@type2x/shared-contracts';

import type { Runtime } from '../runtime.js';
import { useTypingRun } from '../hooks/useTypingRun.js';
import { useErrorSound } from '../hooks/useErrorSound.js';
import { useFocusMode } from './focus-mode.js';
import { KeyboardNotice } from './KeyboardNotice.js';
import { usePlatform } from './platform.js';
import { useSoundOnError } from './sound.js';

export interface TypingScreenProps {
  readonly runtime: Runtime;
  readonly t: Translate;
  readonly title: string;
  readonly hint: string;
  readonly sessionId: SessionId;
  readonly layoutId: LayoutId;
  readonly targetText: string;
  readonly hintLevel: HintLevel;
  /**
   * Экран вложен в макет занятия, который уже назвал режим и задание.
   *
   * Тогда заголовок и подсказка здесь — повтор, стоящий строки над текстом
   * упражнения (ТЗ FR-EN-84). Ярлыками для чтения с экрана они остаются.
   */
  readonly compact?: boolean;
  readonly trackIntegrity?: boolean;
  readonly onFinished: (session: TypingSession, signals: IntegritySignals) => void;
}

/** Режим отрисовки клавиатуры для уровня подсказок. */
function keyboardMode(level: HintLevel): 'full' | 'zone' | 'hidden' {
  if (level === 'full-keyboard-and-finger') return 'full';
  if (level === 'zone-highlight' || level === 'delayed-hint') return 'zone';
  return 'hidden';
}

export function TypingScreen({
  runtime,
  t,
  title,
  hint,
  sessionId,
  layoutId,
  targetText,
  hintLevel,
  compact = false,
  trackIntegrity = false,
  onFinished,
}: TypingScreenProps): React.JSX.Element {
  const focusMode = useFocusMode();
  const platform = usePlatform();
  const playError = useErrorSound(useSoundOnError());
  const run = useTypingRun(runtime, {
    sessionId,
    layoutId,
    platform,
    targetText,
    allowCorrections: hintLevel !== 'no-hints',
    trackIntegrity,
    onError: playError,
    onFinished,
  });

  // Клавиатура и подсказка пальца показываются по раскладке той системы, на
  // которой человек печатает: Apple расставляет часть знаков иначе.
  const layout = getLayout(layoutId, platform);
  const nextCharacter = run.expectedCharacter === '' ? null : run.expectedCharacter;
  const nextFinger = nextCharacter === null ? null : fingerForCharacter(layout, nextCharacter);
  const total = [...targetText].length;

  // Заголовок и подсказка не показываются ни в фокус-режиме, ни внутри макета
  // занятия: в первом случае они соревнуются за внимание с текстом, во втором
  // повторяют сказанное строкой выше. Из разметки они исчезают, но ярлыками
  // для чтения с экрана остаются.
  const quiet = focusMode || compact;

  /*
   * В фокус-режиме остаётся только текст упражнения (ТЗ UI-48).
   *
   * Прогресс, метрики и клавиатура убираются: человек включил режим ровно
   * затем, чтобы с текстом ничего не соревновалось. Место каждого из них в
   * разметке сохраняется — так экран набора не пересоздаётся при включении
   * режима, и измерение продолжается с того же места.
   */
  return (
    <Card title={quiet ? undefined : title}>
      {/*
        Метка раскладки: человек должен знать, в какой раскладке от него ждут
        текст, до первого нажатия, а не после половины упражнения (ТЗ UI-38,
        UI-16). В фокус-режиме и внутри макета занятия её нет: там о раскладке
        говорит сам режим.
      */}
      {!quiet && (
        <p className="t2x-badge t2x-badge--layout">{t(`layoutSwitch.badge.${layout.language}`)}</p>
      )}

      {!quiet && <p className="t2x-muted">{hint}</p>}

      {!focusMode && (
        <ProgressBar
          value={total === 0 ? 0 : run.position / total}
          label={title}
          current={run.position}
          total={total}
        />
      )}

      {/* Печатать нечем — об этом говорится над текстом, а не после того, как
          человек постучал по экрану и ничего не произошло. В фокус-режиме
          тоже: без клавиатуры соревноваться с упражнением нечему. */}
      <KeyboardNotice t={t} />

      <TypingView
        targetText={targetText}
        typedText={run.typedText}
        position={run.position}
        label={title}
      />

      {!focusMode && (
        <MetricsBar
          metrics={run.metrics}
          labels={{
            wpm: t('metrics.wpm'),
            cpm: t('metrics.cpm'),
            accuracy: t('metrics.accuracy'),
            rhythm: t('metrics.rhythm'),
            corrections: t('metrics.corrections'),
          }}
        />
      )}

      {!focusMode && (
        <KeyboardView
          layout={layout}
          nextCharacter={nextCharacter}
          nextFinger={nextFinger}
          mode={keyboardMode(hintLevel)}
        />
      )}
    </Card>
  );
}
